import { useEffect, useRef, useState } from "react";
import {
  Building2,
  Download,
  Globe,
  LifeBuoy,
  Loader2,
  NotebookPen,
  PhoneCall,
  PhoneOutgoing,
  Users,
} from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@wa/ui";
import { leadSearchPath } from "../prospecting/steps";
import { useT } from "../../../i18n/I18nProvider";
import { formatPhone } from "../../../lib/phone";
import {
  callAudioUrl,
  callCounterpart,
  callOwnLine,
  type CallDetail,
  type CallNextStep,
} from "../../../lib/calls";
import type { CrmLabel } from "../../../lib/crmLabels";
import type { PilotChecklistItem } from "../../../lib/callPilot";
import type { LeadStatus } from "../../../lib/clients";
import { Avatar, IconBtn, LabelChips, StatusPill, fmtDateTime, fmtDuration } from "../../ui";
import { PaneBack } from "../_shared/workMobile";
import { CallChecklist } from "./checklist";
import { CallFailure } from "./CallFailure";
import { RecordingRetry } from "./RecordingRetry";
import { CallLinks, callParty, partyPath } from "./CallLinks";
import { CallMenu } from "./CallMenu";
import { CallNote } from "./CallNote";
import { CallFollowUp } from "./CallFollowUp";
import { CallSummary } from "./CallSummary";
import { CallTranscript } from "./CallTranscript";
import { fmtLatency, pilotLatencyAvgMs } from "./pilotLatency";
import { AgentRecording } from "./AgentRecording";
import { RecordingPlayer, type RecordingPlayerHandle } from "./RecordingPlayer";

/** One fact from the call's header row. */
function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <span className="crm-fact">
      <span>{label}</span>
      <b>{children}</b>
    </span>
  );
}

export function CallPane({
  call,
  loading,
  labels,
  checklist,
  retrying,
  onRetry,
  onCallBack,
  onPatch,
  onHandled,
  onTask,
  canMakeTask,
  canHear,
  canMoveLead,
  canReadClients,
  onLeadMoved,
  onBack,
  onErased,
  onRecordingFetched,
}: {
  call: CallDetail | null;
  loading: boolean;
  /** The label registry, for the chips in the header and the menu toggles. */
  labels: CrmLabel[];
  /**
   * The co-pilot checklist, for the coverage block. Passed in rather than
   * fetched here: it is one registry the whole desk shares, and a pane that
   * fetched it would re-request it on every call the operator clicks.
   */
  checklist: PilotChecklistItem[];
  retrying: boolean;
  onRetry: () => void;
  onCallBack: (number: string, companyName?: string) => void;
  onPatch: (body: {
    clientId?: string | null;
    leadId?: string | null;
    contactId?: string | null;
    note?: string | null;
    tags?: string[];
    nextSteps?: CallNextStep[];
    transcriptText?: string | null;
    transcriptSummary?: string | null;
  }) => Promise<void>;
  /** Tick this call off the callback queue, or put it back. Its own callback
   *  rather than a field on `onPatch`: separate endpoint, separate refusal, and
   *  it answers with the queue's size rather than with the row. */
  onHandled: (handled: boolean) => void;
  /** File one next step as a task. Owned by the section, which re-reads after. */
  onTask: (text: string, dueDate: string | null) => Promise<void>;
  /** Creating a task needs manage:crm.projects, which calls alone does not give. */
  canMakeTask: boolean;
  /** May this viewer correct what the model wrote? The browser mirror of
   *  `mayHearCall`; the server refuses anyway, this only hides the pencil. */
  canHear: boolean;
  /** Moving a lead through the funnel needs manage:crm.clients, likewise. */
  canMoveLead: boolean;
  /** Listing a company's people is a read in the clients module (`view:crm`).
   *  Only the picker needs it - the person already on the row came with the
   *  call and is shown either way. */
  canReadClients: boolean;
  /** The lead moved from here; the section holds the copy that has to know. */
  onLeadMoved: (status: LeadStatus) => void;
  /** Back to the call list. A phone shows one pane at a time. */
  onBack: () => void;
  /**
   * A call, or its audio, was erased from the menu here.
   *
   * The pane cannot repair itself: the whole-call case has no row left to draw
   * and the audio case has a stale `recordingStatus` on the copy it is holding.
   * The section owns both the list and the fetch, so it is what re-reads.
   */
  onErased?: (scope: "call" | "recording") => void;
  /**
   * A recording our side had given up on has just been fetched.
   *
   * Its own callback rather than a second meaning for `onErased`: the two do the
   * same thing to the list, and a handler named for erasure being fired when
   * audio ARRIVES is the kind of thing that survives right up until somebody
   * reads it and believes it.
   */
  onRecordingFetched?: () => void;
}) {
  const t = useT();
  // Both strips are opened from the header, and both start closed - including
  // on a call that already has a party or a note. An editor is the answer to
  // "let me change this", not to "who is this about": a search box standing
  // open on every call cost a full row of the pane to re-state a name the facts
  // above it can carry in a corner of one line. The note reads the same way -
  // `CallNote` prints what is stored and only swaps in the textarea on request.
  const [linking, setLinking] = useState(false);
  const [editingNote, setEditingNote] = useState(false);
  // One flag each rather than one shared: correcting the summary and
  // correcting the verbatim transcript are separate acts, and opening one
  // must not silently discard a draft in the other.
  const [editingSummary, setEditingSummary] = useState(false);
  const [editingTranscript, setEditingTranscript] = useState(false);

  /**
   * The handle the transcript steers the recording by.
   *
   * The player owns playback and always did - what it never had was a way to be
   * driven from outside, so a timed transcript on screen could show the second
   * a line was spoken and still leave the operator dragging the waveform to find
   * it. This is the missing direction, and nothing more: `seekTo` moves the
   * playhead and never starts or stops sound.
   */
  const playerRef = useRef<RecordingPlayerHandle>(null);

  const callId = call?.id ?? null;
  useEffect(() => {
    setLinking(false);
    setEditingNote(false);
    // Switching calls closes both correction boxes too. A draft left open would
    // otherwise reopen on the next call with the previous one's text in it, one
    // click away from being saved onto the wrong conversation.
    setEditingSummary(false);
    setEditingTranscript(false);
  }, [callId]);

  if (loading) {
    return (
      <div className="crm-card crm-work-col items-center justify-center">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!call) {
    return (
      <div className="crm-card crm-work-col">
        <div className="crm-pane-none">
          <PhoneCall className="size-8 opacity-50" />
          <span>{t("calls.pane.pickOne")}</span>
        </div>
      </div>
    );
  }

  // The other party and our own line, never a raw column. On an inbound row
  // `toNumber` is OUR Twilio number, so reading it here put our own number in
  // the title, under it, in the copy action and - worst of the four - into the
  // call-back button, which then rang the office back instead of the caller.
  const other = callCounterpart(call);
  const line = callOwnLine(call);
  const inbound = call.direction === "inbound";
  // Who the call is filed against. Stated here rather than left to the picker
  // below, so closing that strip does not hide the one thing it was showing.
  const party = callParty(call);
  const who = call.companyName ?? (other ? formatPhone(other) : t("calls.incoming.withheld"));
  const answered = call.status === "completed";

  // What the co-pilot's loop cost on this call, and whether it kept up. Both
  // read together on purpose - see the fact below.
  const pilotLatencyMs = pilotLatencyAvgMs(call);
  const behind = [
    call.pilotLatencySlowWindows > 0
      ? t("calls.detail.pilotLatencySlow", { slow: String(call.pilotLatencySlowWindows) })
      : null,
    call.pilotWindowsDropped > 0
      ? t("calls.detail.pilotLatencyDropped", { dropped: String(call.pilotWindowsDropped) })
      : null,
  ].filter((part): part is string => part !== null);

  // Three different sentences, and the third is deliberately not "nothing was
  // announced". A call from before the announcement was written down has the
  // same empty columns as one that really said nothing, and the two are not
  // distinguishable from the row - so what is shown is the absence of a record,
  // which is true of both. It is only worth saying at all on a call that kept
  // audio, where somebody will one day ask what the other side was told.
  const noticeFact = call.noticePlayedAt
    ? t(call.noticeVariant === "processed" ? "calls.detail.noticeProcessed" : "calls.detail.noticeRecorded", {
        at: fmtDateTime(call.noticePlayedAt),
      })
    : call.pilotNoticeWaived
      ? t("calls.detail.noticeWaived")
      : call.recordingStatus !== "none"
        ? t("calls.detail.noticeNoRecord")
        : null;

  return (
    <div className="crm-card crm-work-col">
      <div className="crm-read-head">
        {/* The row wraps rather than squeezing. Five controls is 222px of fixed
            width, and in a narrow reading pane that used to leave the company
            name about two characters wide with the number stacked under it three
            lines deep. Below the basis the buttons drop to their own line. */}
        <div className="crm-read-top flex flex-wrap items-center gap-3.5">
          <PaneBack label={t("common.back")} onBack={onBack} />
          <Avatar name={who} size={48} />
          <span className="crm-read-title min-w-0 flex-1">
            <span className="crm-read-subject crm-trunc block">
              {call.regCode ? (
                <Link to={`/ariregister/${call.regCode}`} className="hover:underline">
                  {who}
                </Link>
              ) : (
                who
              )}
            </span>
            <span className="crm-mut crm-trunc mt-[3px] block text-[12.25px] tabular-nums">
              {other ? formatPhone(other) : t("calls.incoming.withheld")}
            </span>
            <LabelChips labels={labels} tags={call.tags} className="mt-2" />
          </span>
          {/* `crm-read-acts` so the phone layout can drop this whole block onto
              its own line, the same way it does on the mail pane. NOT
              `flex-none`: a Tailwind utility sorts after `@layer components`,
              so it beat the `flex: 1 0 100%` that does the dropping however
              specific that rule was made. `.crm-read-acts` is already
              `flex: 0 1 auto`, which is what `flex-none` was there for. */}
          <span className="crm-read-acts flex gap-2">
            {/* Nothing to ring back on a withheld caller id, and a button that
                reports "invalid number" the moment it is pressed is worse than
                one that is not there. */}
            {other ? (
              <IconBtn
                onClick={() => onCallBack(other, call.companyName ?? undefined)}
                title={t("calls.detail.callBack")}
                aria-label={t("calls.detail.callBack")}
              >
                <PhoneOutgoing className="size-4" />
              </IconBtn>
            ) : null}
            <IconBtn
              className={cn((editingNote || call.note) && "is-on")}
              onClick={() => setEditingNote((v) => !v)}
              title={t(call.note ? "calls.detail.editNote" : "calls.detail.addNote")}
              aria-label={t(call.note ? "calls.detail.editNote" : "calls.detail.addNote")}
            >
              <NotebookPen className="size-4" />
            </IconBtn>
            {/* Lit when a party is attached, so the closed strip is still
                visibly the place where that was decided. */}
            <IconBtn
              className={cn((linking || !!party) && "is-on")}
              onClick={() => setLinking((v) => !v)}
              title={t(party ? "calls.detail.changeParty" : "calls.detail.linkParty")}
              aria-label={t(party ? "calls.detail.changeParty" : "calls.detail.linkParty")}
            >
              <Users className="size-4" />
            </IconBtn>
            {/* The support ticket this message was filed as. A way THERE, next
                to the other two cross-links, rather than a copy of the ticket:
                the transcript below is already its first message. */}
            {call.ticket ? (
              <Link
                to={`/support/${call.ticket.id}`}
                className="crm-cb is-on"
                title={t("calls.detail.openTicket", { number: call.ticket.number })}
                aria-label={t("calls.detail.openTicket", { number: call.ticket.number })}
              >
                <LifeBuoy className="size-4" />
              </Link>
            ) : null}
            {call.regCode ? (
              <Link to={`/ariregister/${call.regCode}`} className="crm-cb" title={t("nav.ariregister")}>
                <Building2 className="size-4" />
              </Link>
            ) : null}
            {call.leadDomain ? (
              <Link to={leadSearchPath(call.leadDomain)} className="crm-cb" title={call.leadDomain}>
                <Globe className="size-4" />
              </Link>
            ) : null}
            {/* The header answers "what do I do with this call", the player
                answers "how do I listen to it" - so the download sits here and
                not on the scrubber. */}
            {call.recordingStatus === "stored" ? (
              <a
                href={callAudioUrl(call.id)}
                download
                className="crm-cb"
                title={t("calls.detail.download")}
                aria-label={t("calls.detail.download")}
              >
                <Download className="size-4" />
              </a>
            ) : null}
            <CallMenu
              call={call}
              labels={labels}
              onPatch={onPatch}
              onHandled={onHandled}
              onErased={() => onErased?.("call")}
              onRecordingErased={() => onErased?.("recording")}
            />
          </span>
        </div>

        <div className="crm-facts">
          <Fact label={t("calls.detail.status")}>
            <StatusPill tone={answered ? "ok" : call.status === "no-answer" ? "wt" : "lt"}>
              {t(`calls.status.${call.status}`)}
            </StatusPill>
          </Fact>
          <Fact label={t("calls.detail.duration")}>
            <span className="tabular-nums">{fmtDuration(call.durationSec)}</span>
          </Fact>
          {/* Beside the duration on purpose: both say what SHAPE the call had
              rather than what was in it, and both are absent on plenty of rows
              without anything being wrong.

              ⚠ `!== null` rather than a truthy test. Zero is a real answer -
              the operator said nothing measurable and the customer talked -
              and dropping the fact on that call would hide the single most
              interesting row on the report. */}
          {call.talkRatioPct !== null ? (
            <Fact label={t("calls.detail.talkRatio")}>
              <span className="tabular-nums">
                {t("calls.detail.talkRatioValue", { pct: String(call.talkRatioPct) })}
              </span>
            </Fact>
          ) : null}
          {/* How long the co-pilot took to answer, on calls where it ran.
              Absent on every call it did not, which is most of them.

              ⚠ THE AVERAGE IS NEVER SHOWN ALONE. Twenty fast windows and one
              timeout average to something that reads as healthy, so the counts
              beside it are the part that says the panel fell behind - and
              `dropped` is audio the browser binned rather than wait for, which
              no server-side clock can see at all. Printing the mean by itself
              would be a number that gets more flattering the worse the desk
              performs. */}
          {pilotLatencyMs !== null ? (
            <Fact label={t("calls.detail.pilotLatency")}>
              <span className="tabular-nums">{fmtLatency(pilotLatencyMs)}</span>
              {behind.length > 0 ? <span className="crm-mut"> {behind.join(", ")}</span> : null}
            </Fact>
          ) : null}
          <Fact label={t("calls.detail.when")}>
            <span className="tabular-nums">{fmtDateTime(call.startedAt)}</span>
          </Fact>
          <Fact label={t("calls.detail.source")}>{t(`calls.source.${call.source}`)}</Fact>
          {/* WHAT THE CALLER SAID THEY WANTED, before anybody picked up. Its
              own fact rather than a line in the summary, because it is the one
              thing on this pane that neither an operator nor a model produced -
              somebody pressed a key and said it themselves. Absent when the
              menu was off or nothing was pressed; the fact is left out rather
              than printed empty, because "not asked" is not an answer worth a
              row of its own. */}
          {call.intentDepartment ? (
            <Fact label={t("calls.detail.intentDepartment")}>{call.intentDepartment}</Fact>
          ) : null}
          {/* Who handled it. A fact rather than a line in the header, because
              the header is about the OTHER party - the avatar, the company name
              and the number are all theirs, and an operator name added there
              would read as a second counterparty.

              Unlike on the row, the absence IS printed here. The pane is where
              somebody has come to find out what happened to one specific call,
              and "nobody picked up" is the answer to that question; leaving the
              fact out entirely would look like data we failed to load. */}
          {/* The LABEL follows the direction, because the same column means two
              different things: an outgoing call was placed by its operator, an
              incoming one was answered by whoever picked it up. "Placed by" on
              an inbound row would credit the operator with dialling the person
              who rang them. `calls.detail.who` was an orphan key until now and
              is the outgoing half. */}
          <Fact label={t(inbound ? "calls.detail.answeredBy" : "calls.detail.who")}>
            {call.operatorName ?? (
              <span className="crm-mut">{t("calls.pane.operatorNone")}</span>
            )}
          </Fact>
          {/* Our own line, which is `toNumber` on an inbound call. Printing
              `fromNumber` here labelled the CALLER as the line we answered on. */}
          {line ? (
            <Fact label={t("calls.detail.line")}>
              <span className="tabular-nums">{formatPhone(line)}</span>
            </Fact>
          ) : null}
          {/* The party, as a way THERE rather than as an open editor. Capped in
              width: a fact is one line in a wrapping row, and a long company
              name that pushes the neighbouring facts off the end is worse than
              the same name with an ellipsis on it. */}
          {party ? (
            <Fact label={t(party.kind === "lead" ? "calls.detail.lead" : "calls.detail.client")}>
              <Link to={partyPath(party)} className="crm-trunc block max-w-[220px] hover:underline">
                {party.name}
              </Link>
            </Fact>
          ) : null}
          {/* What the other side was told, from the evidence rather than from
              the settings: `noticeEnabled` is what the call was PLACED with,
              and this is what was served. A call with no record of either shows
              nothing here unless it was recorded, where "we have no record" is
              itself the answer somebody will come asking for. */}
          {noticeFact ? <Fact label={t("calls.detail.notice")}>{noticeFact}</Fact> : null}
          {/* Stated as a fact and not only as the icon above, because on a
              voicemail this is the answer to the question the folder is being
              worked for: has somebody already picked this message up? The
              number is what an operator quotes to a colleague, so it is the
              text of the link rather than a generic word. */}
          {call.ticket ? (
            <Fact label={t("calls.detail.ticket")}>
              <Link to={`/support/${call.ticket.id}`} className="crm-tnum hover:underline">
                {call.ticket.number}
              </Link>
            </Fact>
          ) : null}
        </div>
      </div>

      {linking ? (
        <CallLinks
          call={call}
          canMoveLead={canMoveLead}
          canReadClients={canReadClients}
          onPatch={onPatch}
          onLeadMoved={onLeadMoved}
        />
      ) : null}

      {/* Above the recording strip: on a failed call there is no recording, and
          "this call has no recording" is not the answer the operator came for. */}
      <CallFailure call={call} />

      {call.recordingStatus === "stored" ? (
        <RecordingPlayer
          ref={playerRef}
          callId={call.id}
          duration={call.recordingSec ?? call.durationSec}
          peaks={call.recordingPeaks}
        />
      ) : (
        // Not a plain line any more: on the one class of rows where Twilio still
        // holds the audio our side gave up on, this strip carries the retry.
        <RecordingRetry call={call} onFetched={() => onRecordingFetched?.()} />
      )}

      {/* The AI's own conversation, on the rare call that has two recordings.
          Under the player rather than beside it: the slot above is what the
          caller said to US, and this is what the machine said first. Draws
          nothing at all on every other call. */}
      <AgentRecording call={call} canHear={canHear} />

      <div className="crm-read-body">
        {/* Above the AI summary on purpose: what the operator concluded outranks
            what the model concluded. */}
        <CallNote
          note={call.note}
          editing={editingNote}
          onEditing={setEditingNote}
          onSave={(value) => onPatch({ note: value })}
        />

        <CallSummary
          summary={call.transcriptSummary}
          objections={call.objections}
          steps={call.transcriptNextSteps}
          stepTasks={call.stepTasks}
          canMakeTask={canMakeTask}
          editing={editingSummary}
          onEditing={setEditingSummary}
          editedAt={call.transcriptEditedAt ? fmtDateTime(call.transcriptEditedAt) : null}
          editedBy={call.transcriptEditedName ?? null}
          onSaveSummary={
            canHear ? (value) => onPatch({ transcriptSummary: value }) : undefined
          }
          onSteps={(nextSteps) => onPatch({ nextSteps })}
          onTask={onTask}
        />

        {/* Under the summary it is written FROM, and only for a viewer who may
            read that summary: the letter is the conversation in other words,
            and the server refuses the same request from anybody else. */}
        {canHear ? (
          <CallFollowUp
            callId={call.id}
            hasSummary={!!call.transcriptSummary?.trim()}
            hasDraft={!!call.followUpDraft}
          />
        ) : null}

        {/* Between what the call concluded and what was said. It is a reading of
            the transcript, so it belongs under the other reading and above the
            words both of them came from. */}
        <CallChecklist
          items={checklist}
          answered={call.checklistAnswered}
          readAt={call.checklistReadAt}
        />

        <CallTranscript
          call={call}
          retrying={retrying}
          onRetry={onRetry}
          editing={editingTranscript}
          onEditing={setEditingTranscript}
          onSaveText={canHear ? (value) => onPatch({ transcriptText: value }) : undefined}
          // Only when there is a player above to steer. On every other row the
          // recording strip is a retry button or a sentence, so a timestamp
          // would be a control that does nothing - and the operator would be
          // right to read that as broken rather than as "no audio here".
          onSeek={
            call.recordingStatus === "stored"
              ? (seconds) => playerRef.current?.seekTo(seconds)
              : undefined
          }
        />
      </div>
    </div>
  );
}
